import React from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Sheet,
    View,
    YStack,
} from 'smoothie';

import type { FreeToSpendPeriod } from '../../types/FreeToSpendTypes';

export interface SpendingStatusSheetProps {
    isOpen: boolean;
    onClose: () => void;
    period: FreeToSpendPeriod;
}

const getStatusItems = (
    period: FreeToSpendPeriod,
): Array<{
    label: string;
    description: string;
    backgroundColor: string;
    labelColor: string;
}> => {
    const isMonthly = period === 'monthly';
    const periodLabel = isMonthly ? 'month' : 'week';
    const periodAdjective = isMonthly ? 'monthly' : 'weekly';
    return [
        {
            label: 'On track',
            description: `Your spending is on pace with your budget for this ${periodLabel}.`,
            backgroundColor: '$green900',
            labelColor: '$white',
        },
        {
            label: 'Slow down',
            description: `Your spending is trending above the daily average needed to stay within your ${periodAdjective} budget.`,
            backgroundColor: '$lemon',
            labelColor: '$lemonDarkest',
        },
        {
            label: 'Over',
            description: `You've exceeded your ${periodAdjective} budget.`,
            backgroundColor: '$orange400',
            labelColor: '$orange1100',
        },
    ];
};

export const SpendingStatusSheet = ({
    isOpen,
    onClose,
    period,
}: SpendingStatusSheetProps) => {
    const statusItems = getStatusItems(period);
    const isMonthly = period === 'monthly';
    const disclaimer = isMonthly
        ? 'Spending status only applies to the current month and does not consider previous months.'
        : 'Spending status only applies to the current week and does not consider previous weeks.';

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Spending status"
            showCloseButton
        >
            <YStack flex={1}>
                <YStack gap={16} marginBottom={24}>
                    {statusItems.map((item) => (
                        <View
                            key={item.label}
                            backgroundColor="$gray200"
                            borderRadius={24}
                            gap={8}
                            padding={16}
                        >
                            <View
                                alignSelf="center"
                                backgroundColor={item.backgroundColor}
                                borderRadius={9999}
                                paddingHorizontal={12}
                                paddingVertical={4}
                            >
                                <BodyMediumRegular
                                    color={item.labelColor}
                                    fontSize={16}
                                    fontWeight="600"
                                    lineHeight={24}
                                >
                                    {item.label}
                                </BodyMediumRegular>
                            </View>
                            <BodyMediumRegular
                                color="$gray1000"
                                textAlign="center"
                            >
                                {item.description}
                            </BodyMediumRegular>
                        </View>
                    ))}
                    <BodyMediumRegular color="$gray1000" textAlign="center">
                        {disclaimer}
                    </BodyMediumRegular>
                </YStack>
                <Button
                    text="Got it"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={onClose}
                />
            </YStack>
        </Sheet>
    );
};
